import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [reading, setReading] = useState(null);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out. Please contact support with your session ID.');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        timeout: 5000
      });
      
      if (response.data.payment_status === 'paid') {
        setStatus('paid');
        await generateReading(sessionId);
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired. Please try again.');
      } else {
        setTimeout(() => {
          pollPaymentStatus(sessionId, attempts + 1).catch(err => {
            console.error('Polling error:', err);
            setStatus('error');
            toast.error('Failed to verify payment. Please refresh the page.');
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Status check error:', error);
      const status = error.response?.status;
      const errorMessage = error.response?.data?.detail || 'Network error occurred';
      
      if (status === 400 || status === 404) {
        setStatus('error');
        toast.error(`Invalid payment session: ${errorMessage}. Please contact support.`);
        return;
      }
      
      if (attempts < maxAttempts - 1) {
        setTimeout(() => {
          pollPaymentStatus(sessionId, attempts + 1).catch(err => {
            console.error('Retry error:', err);
          });
        }, 2000);
      } else {
        setStatus('error');
        toast.error(`Payment verification failed: ${errorMessage}. Please contact support.`);
      }
    }
  };

  const generateReading = async (sessionId) => {
    try {
      const pendingReading = JSON.parse(sessionStorage.getItem('pendingReading') || '{}');
      
      if (!pendingReading.name || !pendingReading.birthday) {
        setStatus('error');
        toast.error('Reading data not found. Please start over.');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      setStatus('generating');
      
      const response = await axios.post(`${API}/generate-reading`, {
        name: pendingReading.name,
        birthday: pendingReading.birthday,
        session_id: sessionId
      }, {
        timeout: 60000
      });

      if (!response.data || !response.data.reading_id) {
        throw new Error('Invalid reading data received');
      }

      setReading(response.data);
      setStatus('complete');
      sessionStorage.removeItem('pendingReading');
      
      setTimeout(() => {
        navigate(`/reading/${response.data.reading_id}`);
      }, 2000);
    } catch (error) {
      console.error('Reading generation error:', error);
      setStatus('error');
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error occurred';
      toast.error(`Failed to generate reading: ${errorMsg}. Please contact support with your payment confirmation.`);
    }
  };

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/');
      return;
    }

    pollPaymentStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-void text-text-primary flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full text-center"
        data-testid="success-page-container"
      >
        {status === 'checking' && (
          <div data-testid="payment-checking-status">
            <Loader2 className="w-16 h-16 text-gold mx-auto mb-6 animate-spin" />
            <h2 className="font-playfair text-2xl font-bold mb-2">Verifying Payment</h2>
            <p className="text-text-secondary">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'paid' && (
          <div data-testid="payment-success-status">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h2 className="font-playfair text-2xl font-bold mb-2">Payment Successful!</h2>
            <p className="text-text-secondary">Preparing your reading...</p>
          </div>
        )}

        {status === 'generating' && (
          <div data-testid="reading-generating-status">
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-gold/20" />
              <div className="absolute inset-0 rounded-full border-4 border-gold border-t-transparent animate-spin" />
              <div className="absolute inset-4 rounded-full bg-gold/20 animate-pulse-glow" />
            </div>
            <h2 className="font-playfair text-2xl font-bold mb-2">Channeling Your Energy</h2>
            <p className="text-text-secondary">Our AI numerologist is creating your personalized reading...</p>
          </div>
        )}

        {status === 'complete' && (
          <div data-testid="reading-complete-status">
            <CheckCircle className="w-16 h-16 text-gold mx-auto mb-6" />
            <h2 className="font-playfair text-2xl font-bold mb-2">Your Reading is Ready!</h2>
            <p className="text-text-secondary">Redirecting you now...</p>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="payment-error-status">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h2 className="font-playfair text-2xl font-bold mb-2">Something Went Wrong</h2>
            <p className="text-text-secondary mb-6">We encountered an issue processing your request.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-gold text-void hover:bg-gold-hover rounded-full px-8 py-3 font-medium transition-all"
              data-testid="return-home-button"
            >
              Return Home
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SuccessPage;
